<template>
    <div class="slice page-all-freelancers">
        <div class="slice-content text-left">
            <h1 class="slice-title">{{ title }}</h1>
            <div class="slice-body page-all-freelancers__items">
                <template v-for="(profile, i) in freelancers" :key="i">
                    <NuxtLink :to="$withLocalePrefix(profile.url)" external class="freelancer">
                        <strong>{{ profile.firstName }} {{ profile.lastNameNormalized }}</strong>
                        <div>{{ profile.headline }}</div>
                    </NuxtLink>
                </template>
            </div>
            <MktPagination v-if="pagination && pagination.total > 1" :current-page="pagination.current" :total-pages="pagination.total" />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import {ref, watch} from 'vue';
    import {MktPagination} from '#components';
    import {createError, useAsyncData, useLocalePrefix, useRoute, useTranslation, useSeoMeta, useHead, useRequestURL} from '#imports';
    import {definePageMeta} from '#imports';
    import {AllFreelancersSearchApi, Configuration, type AllFreelancersResponse, type FreelancerProfile, type PaginationResponse} from '@api';

    definePageMeta({
        middleware: ['p1-query-redirect'],
    });

    const {t} = useTranslation();
    const route = useRoute();
    const searchApi = new AllFreelancersSearchApi(new Configuration({basePath: ''}));
    const {getLocalePrefix, getLocaleCodeFromPrefix} = useLocalePrefix();
    const localePrefix = getLocalePrefix();

    const freelancers = ref<FreelancerProfile[]>([]);
    const pagination = ref<PaginationResponse>();
    const title = t('search.result.title', ['']);
    const description = t('search.meta.description', ['', '']);

    function fetchData() {
        const page = Number(route.query.p as string);
        const searchRequest = {
            p: page > 0 ? page : 1,
            size: 400,
            locale: localePrefix ? getLocaleCodeFromPrefix(localePrefix) : undefined,
        };
        return searchApi.searchAllFreelancers(searchRequest);
    }

    function updateData(data: AllFreelancersResponse) {
        freelancers.value = data.profileList.content;
        pagination.value = data.profileList.pagination;
    }

    const {data, error} = await useAsyncData('all-freelancers', fetchData, {deep: true});

    if (!data.value || error.value) {
        throw createError({statusCode: 404, statusMessage: 'Error fetching all freelancers ' + error?.value});
    }
    updateData(data.value);

    // csr (page change)
    watch(
        () => route.query,
        async () => {
            updateData(await fetchData());
            window.scrollTo({top: 0, behavior: 'smooth'});
        },
    );

    // Canonical
    const {origin, pathname} = useRequestURL();
    const canonicalParams = route.query.p ? `?p=${route.query.p}` : '';
    const canonicalUrl = `${origin}${pathname}${canonicalParams}`;

    useSeoMeta({title, description});
    useHead({link: [{rel: 'canonical', href: canonicalUrl}]});
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .hidden-link {
        display: none;
    }

    .page-all-freelancers {
        &__items {
            display: grid;
            gap: var(--joy-core-spacing-4);
            grid-template-columns: repeat(1, minmax(0, 1fr));

            @include mq.screen_xs(min) {
                grid-template-columns: repeat(2, minmax(0, 1fr));
            }
            @include mq.screen_sm(min) {
                gap: var(--joy-core-spacing-6);
                grid-template-columns: repeat(3, minmax(0, 1fr));
            }
            @include mq.screen_lg(min) {
                grid-template-columns: repeat(4, minmax(0, 1fr));
            }
        }
        .joy-pagination {
            display: inline-flex;
            margin: var(--joy-core-spacing-8) auto 0;
        }
        .freelancer {
            color: var(--joy-color-neutral-60);

            strong {
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-primary-400);

                @include mq.screen_sm(min) {
                    font-size: var(--joy-font-size-primary-600);
                }
            }
            div {
                color: var(--joy-color-neutral-40);
                font-size: var(--joy-font-size-primary-300);
            }

            &:hover {
                strong {
                    text-decoration: underline;
                }
                div {
                    color: var(--joy-color-neutral-60);
                }
            }
        }
    }
</style>
